import api from 'api'
import { HeadingSection, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useFlags } from 'config/useFlagsContext'
import { useSolicitacoesExameUnpagedQuery } from 'graphql/hooks.generated'
import { RastreioCancerInput } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { Fragment, useCallback, useMemo } from 'react'
import { isEmpty } from 'util/checks'
import { MetaArray } from 'util/metaPath'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME } from '../tabs'
import { AdicionarSolicitacaoExameButtons } from './AdicionarSolicitacaoExameButtons'
import {
  convertHistoricoSolicitacaoExameToImpressao,
  convertSolicitacaoFromAtendToSolicitacaoWithInfo,
} from './converter-solicitacaoExames'
import SolicitacaoExamesTable from './ExamesTable'
import { CODIGO_CITOPATOLOGICOS, CODIGO_MAMOGRAFIAS, SolicitacaoExameModel } from './model-solicitacaoExames'
import { SolicitarExamesRootView } from './SolicitarExamesRootView'
import {
  getRastreioCitoPreenchido,
  getRastreioMamografiaPreenchido,
  joinSolicitacoesExame,
} from './utils-solicitacaoExames'

export interface ExamesViewProps {
  name: MetaArray<SolicitacaoExameModel>
  cidadao: CidadaoAtendimento
  prontuarioId: ID
  atendimentoId: ID
  atendimentoProfissional: AtendimentoProfissional
  isObservacaoAndAuxiliar: boolean
}

export function ExamesView(props: ExamesViewProps) {
  const { name, cidadao, prontuarioId, atendimentoId, atendimentoProfissional, isObservacaoAndAuxiliar } = props

  const alert = useAlert()
  const { analytics } = useFirebase()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()
  const { PILOTO_SOLICITACAO_EXAME_CITOPATOLOGICO_UTERINO_ENABLED, SOLICITACAO_EXAME_MAMOGRAFIA_ENABLED } = useFlags()

  const {
    observacao: { isAtendimentoObservacao },
    tiposAtendimento: { isAtendimentoProcedimentos },
  } = useAtendimentoContext()

  const {
    handleSubmit,
    removeItem,
    handleRowChanged,
    input: { value: solicitacoesExameFromAtend },
  } = useEditableListField<SolicitacaoExameModel>({
    name: name,
    subscription: { value: true },
  })

  const handleUpdate = useCallback(
    (formValues: SolicitacaoExameModel) => {
      const hasCitopatologico = formValues.examesSolicitados?.some(
        (exame) => exame.exame && CODIGO_CITOPATOLOGICOS.includes(exame.exame.codigo)
      )
      const hasMamografia = formValues.examesSolicitados?.some(
        (exame) => exame.exame && CODIGO_MAMOGRAFIAS.includes(exame.exame.codigo)
      )

      handleRowChanged({
        ...formValues,
        rastreioCitopatologicoColoUtero: hasCitopatologico ? formValues.rastreioCitopatologicoColoUtero : null,
        rastreioMamografia: hasMamografia ? formValues.rastreioMamografia : null,
      })
    },
    [handleRowChanged]
  )

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissional.id)

  const {
    data: { solicitacoesExame },
    loading,
  } = useSolicitacoesExameUnpagedQuery({
    variables: {
      input: {
        prontuarioId,
        editAtendProfId,
      },
    },
  })

  const hasSolicitacaoExames = !isEmpty(solicitacoesExameFromAtend)

  const itemsToPrint = convertSolicitacaoFromAtendToSolicitacaoWithInfo(solicitacoesExameFromAtend, {
    dataReferencia: atendimentoProfissional.iniciadoEm,
    atendimentoProfissionalId: atendimentoProfissional.id,
    lotacao: atendimentoProfissional.lotacao,
  })

  const allContent = useMemo(
    () =>
      joinSolicitacoesExame(solicitacoesExame?.content, solicitacoesExameFromAtend, {
        dataReferencia: atendimentoProfissional.iniciadoEm,
        atendimentoProfissionalId: atendimentoProfissional.id,
        lotacao: atendimentoProfissional.lotacao,
      }),
    [
      atendimentoProfissional.id,
      atendimentoProfissional.iniciadoEm,
      atendimentoProfissional.lotacao,
      solicitacoesExame,
      solicitacoesExameFromAtend,
    ]
  )

  const handleImpressaoExames = () => {
    analytics.logEvent('imprimir_solicitacoes_EXM')

    const input = convertHistoricoSolicitacaoExameToImpressao(itemsToPrint, prontuarioId, atendimentoId, cidadao.id)
    const citopatologicoPreenchido: ReadonlyArray<RastreioCancerInput> = PILOTO_SOLICITACAO_EXAME_CITOPATOLOGICO_UTERINO_ENABLED
      ? getRastreioCitoPreenchido(itemsToPrint)
      : []
    const mamografiaPreenchido: ReadonlyArray<RastreioCancerInput> = SOLICITACAO_EXAME_MAMOGRAFIA_ENABLED
      ? getRastreioMamografiaPreenchido(itemsToPrint)
      : []

    handleImpressaoPath({
      apiPath: api.solicitacaoExames.imprimirSolicitacaoExames,
      values: {
        solicitacoes: input,
        citopatologicoPreenchido: citopatologicoPreenchido,
        mamografiaPreenchido: mamografiaPreenchido,
      },
      file: 'Imprimir solicitação',
    })
      .then(() => alert('success', 'Geração da impressão de solicitações ocorreu com sucesso.'))
      .catch((error) => handleImpressaoError(error, 'Falha na impressão da solicitação.'))
  }

  const imprimirTooltip = !hasSolicitacaoExames
    ? `Não foram realizadas solicitações neste atendimento.`
    : 'Imprimir todas as solicitações deste atendimento.'
  const disabledImpressao = !hasSolicitacaoExames

  return (
    <Fragment>
      <HeadingSection level={4} vSpace={8} title={SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME} />
      <VFlow>
        {!isAtendimentoProcedimentos && <AdicionarSolicitacaoExameButtons />}
        <SolicitarExamesRootView
          cidadao={cidadao}
          handleSubmit={handleSubmit}
          handleUpdate={handleUpdate}
          adicionados={solicitacoesExameFromAtend}
          existentes={solicitacoesExame?.content}
          iniciadoEm={atendimentoProfissional.iniciadoEm}
        />

        <SolicitacaoExamesTable
          atendimentoProfissionalId={atendimentoProfissional.id}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          removeItem={removeItem}
          loading={loading}
          cidadaoId={cidadao.id}
          dataReferencia={atendimentoProfissional.iniciadoEm}
          lotacao={atendimentoProfissional.lotacao}
          isObservacaoAndAuxiliar={isObservacaoAndAuxiliar}
          items={allContent}
          solicitacoesExameFromAtend={solicitacoesExameFromAtend}
          imprimirRegistradoAgora={{
            tooltip: imprimirTooltip,
            disabled: disabledImpressao,
            handleImpressao: handleImpressaoExames,
          }}
        />
      </VFlow>
    </Fragment>
  )
}
